import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, MessageCircle, MapPin, Video, Languages, ShoppingBasket, HeartHandshake } from "lucide-react";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";

// English landing page: the only page of the site written for people searching in English
// (partners, employers, GPs or Brazilians who search in English).
export const faqItems = [
  {
    question: "Are consultations in English or Portuguese?",
    answer:
      "Consultations are held in Portuguese, so Brazilian clients can explain habits, cravings and symptoms in their own language. Enquiries and messages in English are welcome, for example from a partner or employer booking on someone's behalf.",
  },
  {
    question: "Where are you based?",
    answer:
      "In Hamilton, Brisbane (8 Harbour Rd, QLD 4007), with in-person appointments. Online consultations by video call are available Australia-wide: Sydney, Melbourne, Perth, Adelaide, Gold Coast and regional areas.",
  },
  {
    question: "How does the first consultation work?",
    answer:
      "It is a 30-minute conversation, at no charge, in person in Hamilton or by video call. I get to know your routine and goals and explain how the follow-up program works. Afterwards, you decide in your own time whether to continue.",
  },
  {
    question: "Are you a dietitian?",
    answer:
      'I am a qualified nutritionist (nutricionista) working with Brazilian clients in Portuguese. In Australia, "dietitian" refers to a separate accreditation (Accredited Practising Dietitian), which this service does not include. If you need clinical dietetic treatment or a Medicare-rebated referral, I will tell you so in the first conversation.',
  },
  {
    question: "Can I claim Medicare or private health rebates?",
    answer:
      "Medicare rebates apply only to Accredited Practising Dietitians, so they do not apply here. Private health extras depend on your fund's cover for nutrition services: I provide a receipt and you can check with your fund. Fees are set with no rebate in mind.",
  },
  {
    question: "What does the follow-up program include?",
    answer:
      "A personalised meal plan built with products from Coles, Woolworths and Aldi, access to an online platform with practical content, direct WhatsApp support between appointments and regular adjustments as your routine changes.",
  },
  {
    question: "How much does it cost?",
    answer:
      "Pricing depends on the plan and how often we meet. I go through the options and fees during the initial consultation, so you can decide with all the information.",
  },
];

const highlights = [
  {
    icon: Languages,
    title: "Nutrition coaching in Portuguese",
    text: "For Brazilians living in Australia who want guidance in their own language, from someone who went through the same adaptation.",
  },
  {
    icon: ShoppingBasket,
    title: "Built around Australian supermarkets",
    text: "Meal plans use what you actually find at Coles, Woolworths and Aldi, adapted to Australian prices and routines.",
  },
  {
    icon: HeartHandshake,
    title: "Ongoing support, not a diet sheet",
    text: "Platform access, WhatsApp support between sessions and plan adjustments as work, study or shift patterns change.",
  },
];

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <div className="min-h-screen" lang="en">
      <Navigation />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
          <div
            className="absolute inset-0 opacity-15"
            style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
            <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
              Clara Camille
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
              Portuguese-speaking nutritionist in Brisbane
            </h1>
            <p className="text-base sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
              Nutrition coaching for Brazilians living in Australia. In person in Hamilton, Brisbane, and online
              Australia-wide.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg w-full sm:w-auto"
                style={{ background: "var(--gradient-green)" }}
                onClick={openDialog}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a consultation
              </Button>
              <Button
                size="lg"
                variant="gradient-outline"
                className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
                onClick={() => window.open("https://wa.me/61423038977", "_blank")}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Message on WhatsApp
              </Button>
            </div>
            <p className="text-sm text-muted-foreground font-sans">30-minute initial consultation, no charge.</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground font-sans">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-primary" /> Hamilton, Brisbane QLD
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Video className="h-4 w-4 text-primary" /> Online across Australia
              </span>
            </div>
          </div>
        </section>

        {/* WHO IT IS FOR */}
        <section className="bg-card py-14 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
              <div className="w-full lg:w-1/2 space-y-5">
                <h2 className="text-2xl sm:text-4xl font-typewriter font-bold text-foreground">Who this is for</h2>
                <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
                  Most of my clients are Brazilians who moved to Australia for work or study and found it hard to keep
                  eating well with the new routine: shift work in hospitality, unfamiliar supermarkets, food labels in
                  English and the comfort eating that comes with homesickness.
                </p>
                <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
                  If you are an English-speaking partner, employer or GP looking for a nutritionist who can work with a
                  Brazilian client in Portuguese, this page is for you. I speak the language and I know the context, so
                  nothing gets lost in translation.
                </p>
              </div>
              <div className="w-full lg:w-1/2 flex justify-center">
                <img
                  src={claraPortrait}
                  alt="Clara Camille, Portuguese-speaking nutritionist in Brisbane"
                  className="max-h-[420px] w-auto object-contain"
                  width={420}
                  height={420}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="bg-background py-14 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
              {highlights.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="p-5 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 space-y-3">
                    <Icon className="h-6 w-6 text-primary" />
                    <h3 className="text-lg font-typewriter font-bold text-foreground">{item.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="bg-card py-14 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl space-y-8">
            <h2 className="text-2xl sm:text-4xl font-typewriter font-bold text-foreground text-center">How it works</h2>
            <ol className="space-y-4">
              {[
                [
                  "30-minute initial consultation",
                  "In person in Hamilton or by video call. We talk about your routine, goals and what has not worked so far.",
                ],
                [
                  "Personalised plan",
                  "A meal plan built with Australian supermarket products, your schedule and your budget, delivered in Portuguese.",
                ],
                [
                  "Ongoing follow-up",
                  "Regular appointments, WhatsApp support between them and adjustments whenever your routine changes.",
                ],
              ].map(([title, text], index) => (
                <li key={title} className="flex gap-4 p-4 sm:p-5 rounded-xl bg-background border border-border">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground font-sans">{title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background py-14 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
            <h2 className="text-2xl sm:text-4xl font-typewriter font-bold text-foreground text-center mb-8 sm:mb-12">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="border border-border rounded-xl px-4 sm:px-6 bg-card"
                >
                  <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 opacity-80" style={{ background: "var(--gradient-green)" }} />
          <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-2xl text-center space-y-6">
            <h2 className="text-2xl sm:text-4xl font-typewriter font-bold text-white leading-tight">
              Book an initial consultation
            </h2>
            <p className="text-base sm:text-lg text-white/90 font-sans">
              Thirty minutes, in Portuguese, in Hamilton or online. We look at your routine and goals, and I explain how
              the follow-up program works.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                onClick={openDialog}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a consultation
              </Button>
              <Button
                size="lg"
                className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                style={{ background: "rgba(255, 255, 255, 0.2)", backdropFilter: "blur(12px)", color: "white" }}
                onClick={() => window.open("https://wa.me/61423038977", "_blank")}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingChat />
      <ScheduleDialog open={open} onOpenChange={setOpen} />
    </div>
  );
};

const PortugueseSpeakingNutritionist = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default PortugueseSpeakingNutritionist;
